import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import VuiButton from "components/VuiButton";
import { selectedMerchant as setSelectedMerchant } from "redux/actions";
import { useApi } from "hooks/useAPI";
import AppService from "../../services/AppService";
import { DisconnectResponse, DisconnectErrorResponse, DisconnectRequestData } from "../../types/quickbooks";

interface QuickbooksDisconnectProps {
  merchant: any;
}

const QuickbooksDisconnect = ({ merchant }: QuickbooksDisconnectProps) => {
  const { getToken } = useApi();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleDisconnectClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmDisconnect = async () => {
    if (!merchant?.id) {
      enqueueSnackbar("Merchant ID is required", { variant: "error" });
      return;
    }

    setLoading(true);
    try {
      const token = getToken ? await getToken() : "";
      if (!token) {
        enqueueSnackbar("Authentication required", { variant: "error" });
        return;
      }

      const requestData: DisconnectRequestData = {
        merchantId: merchant.id,
      };

      const response = await AppService.quickbooksDisconnect(requestData, token) as unknown as DisconnectResponse;

      dispatch(setSelectedMerchant({
        ...merchant,
        quickbooksConnected: response.quickbooksConnected,
        quickbooksTokenPeriod: response.quickbooksTokenPeriod,
      }));

      enqueueSnackbar(response.message || "QuickBooks disconnected successfully", { 
        variant: "success" 
      });

      setShowConfirmDialog(false);
    } catch (error: any) {
      console.error("QuickBooks disconnect failed:", error);
      
      let errorMessage = "Failed to disconnect QuickBooks";
      
      if (error?.response?.data) {
        const errorData = error.response.data as DisconnectErrorResponse;
        errorMessage = errorData.message || errorMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDisconnect = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      {/* @ts-ignore */}
      <VuiButton
        fullWidth
        variant="outlined"
        color="error"
        onClick={handleDisconnectClick}
        disabled={loading}
      >
        Disconnect
      </VuiButton>

      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelDisconnect}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.87)",
            color: "white",
          },
        }}
      >
        <DialogTitle sx={{ color: "white !important" }}>
          Disconnect QuickBooks
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "white", mb: 2 }}>
            Are you sure you want to disconnect QuickBooks from your store?
          </Typography>
          <Typography sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "0.875rem" }}>
            This will remove the QuickBooks integration and you'll need to reconnect to access QuickBooks data again.
          </Typography>
        </DialogContent>
        <DialogActions>
          {/* @ts-ignore */}
          <VuiButton 
            variant="outlined" 
            onClick={handleCancelDisconnect}
            disabled={loading}
          >
            Cancel
          </VuiButton>
          {/* @ts-ignore */}
          <VuiButton
            variant="contained"
            color="error"
            onClick={handleConfirmDisconnect}
            loading={loading}
          >
            Disconnect
          </VuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuickbooksDisconnect; 